/*
 * The sign-in screen's layout.
 *
 * Every value here is a TOKEN, and the token names are the real ones
 * (`packages/experience/src/tokens.ts` cssVars + `packages/ui/src/global.css`).
 * That is a fix, not housekeeping: this file used to reference `--surface-sunken`,
 * `--text`, `--text-muted`, `--danger-text`, `--space-6`, `--radius-lg` and
 * `--font-size-xl` — none of which are declared anywhere in the repo. Every one
 * fell through to its hardcoded hex, in BOTH themes, so the login screen was the
 * one surface a brand could not reach and dark mode did not apply to it at all.
 *
 * No custom property is set from JSX, so nothing here needs the material suite's
 * LOCAL escape hatch.
 */

.page {
  min-height: calc(100vh - var(--shell-footer-h, 0px));
  min-height: calc(100dvh - var(--shell-footer-h, 0px));
  display: grid;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-sans);
}

/* Two columns only when there IS a second column (`data-aside` on the host).
   The brand panel is the wider side, as it is on the reference screen. */
@media (min-width: 900px) {
  .page[data-aside] { grid-template-columns: 1.05fr 0.95fr; }
}

/* ── the brand panel ─────────────────────────────────────────────────────────
   Hidden below the breakpoint rather than stacked: on a phone it would push the
   form under a screenful of marketing, and the lockup already comes with the
   card there. */
.aside {
  display: none;
  position: relative;
  overflow: hidden;
  padding: var(--sp-12) var(--sp-12);
  place-items: center;
  border-right: 1px solid var(--border);
  background: var(--base);
}

@media (min-width: 900px) {
  .page[data-aside] .aside { display: grid; }
}

/* The washes: the brand's own two colours, bled through the stone. `color-mix`
   keeps them the BRAND's — a rebrand moves them, and nothing here is a hex. */
.glowBrand,
.glowAccent {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
}
/* The mix percentages are low on purpose. The reference screen
   (portal.moonbase.be) paints these at 0.22/0.18 over near-black ink, where a
   wash reads as a glow; over the light `--base` the same strength reads as a
   solid colour field and swallows the panel. These are tuned to the LIGHT side,
   which is the brand's first-load mode since 2026-08-03, and stay legible on
   dark because they sit under the ink rather than over it. */
.glowBrand {
  inset-block-start: -13rem;
  inset-inline-end: -9rem;
  inline-size: 30rem;
  block-size: 30rem;
  background: color-mix(in srgb, var(--brand) 18%, transparent);
}
.glowAccent {
  inset-block-end: -9rem;
  inset-inline-start: -8rem;
  inline-size: 24rem;
  block-size: 24rem;
  background: color-mix(in srgb, var(--accent) 14%, transparent);
}

/* A wash is decoration; a person who has asked for less motion has usually also
   asked for less of this. Nothing animates here, but the blur is expensive on
   low-end hardware and contributes nothing semantically. */
@media (prefers-reduced-transparency: reduce) {
  .glowBrand,
  .glowAccent { display: none; }
}

.asideInner {
  position: relative;
  inline-size: 100%;
  max-inline-size: 29rem;
}

.asideLogo {
  display: block;
  block-size: 34px;
  inline-size: auto;
  margin-block-end: var(--sp-10);
}

.headline {
  margin: 0;
  font-size: var(--fs-3xl);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.025em;
  color: var(--ink);
}

/* The last line takes the brand gradient — the same paint as the primary
   button, so the two agree without either naming a colour. `color` is set first
   and stays the fallback: where `background-clip: text` is unsupported the line
   is still ink rather than invisible. */
.headlineAccent {
  color: var(--brand-text);
  background: var(--brand-gradient, var(--brand));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

@supports not (background-clip: text) {
  .headlineAccent { -webkit-text-fill-color: currentcolor; }
}

.tagline {
  margin: var(--sp-4) 0 0;
  max-inline-size: 27rem;
  font-size: var(--fs-lg);
  line-height: 1.5;
  color: var(--muted);
}

.assurance {
  margin: var(--sp-10) 0 0;
  font-size: var(--fs-xs);
  letter-spacing: 0.02em;
  color: var(--muted);
}

/* ── the form side ───────────────────────────────────────────────────────── */
.formSide {
  display: grid;
  grid-template-rows: 1fr auto;
  justify-items: center;
  align-items: center;
  padding: var(--sp-6);
  gap: var(--sp-6);
}

.card {
  inline-size: 100%;
  max-inline-size: 24rem;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-6);
  border: 1px solid var(--hair);
  border-radius: var(--r-lg);
  background: var(--surface);
  box-shadow: inset 0 1px 0 var(--rim), var(--e2);
}

/* The card carries no chrome of its own when it is beside the brand panel —
   the panel is the frame, and a second bordered box inside it reads as a dialog
   that arrived by mistake. */
@media (min-width: 900px) {
  .page[data-aside] .card {
    border-color: transparent;
    background: none;
    box-shadow: none;
    padding: 0;
  }
}

.cardLogo {
  display: block;
  /* The card is a flex COLUMN, so a stretched item would centre a fixed-height
     image across the full width and the lockup would not line up with the
     heading beneath it. */
  align-self: flex-start;
  block-size: 30px;
  inline-size: auto;
  margin-block-end: var(--sp-2);
}

@media (min-width: 900px) {
  .page[data-aside] .cardLogo { display: none; }
}

/* The compact lockup's text half (#2296). Sized and spaced like the logo it
   stands in for, so a phone gets the deployment's identity whether the brand
   ships a mark or only a name. */
.cardBrand {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-lg);
  font-weight: 760;
  letter-spacing: -0.01em;
  color: var(--brand-text, var(--ink));
}
@media (min-width: 900px) { .page[data-aside] .cardBrand { display: none; } }

.title {
  margin: 0;
  font-size: var(--fs-2xl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--ink);
}

.sub {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--muted);
}

.error {
  font-size: var(--fs-sm);
  color: var(--crit);
  background: var(--crit-bg);
  border: 1px solid color-mix(in srgb, var(--crit) 35%, transparent);
  border-radius: var(--r-sm);
  padding: var(--sp-2) var(--sp-3);
}

.confirmation {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  color: var(--ink);
  background: var(--good-bg);
  border: 1px solid color-mix(in srgb, var(--good) 35%, transparent);
  border-radius: var(--r-md);
  padding: var(--sp-4);
  line-height: 1.5;
}

.confirmation span { color: var(--muted); font-size: var(--fs-sm); }

/* ── the legal footer ────────────────────────────────────────────────────── */
.legal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  padding-block-end: var(--sp-2);
  text-align: center;
}

.legalLinks {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--sp-1) var(--sp-4);
  font-size: var(--fs-xs);
}

.legalLinks a {
  color: var(--muted);
  text-decoration: none;
  border-radius: var(--r-sm);
}

.legalLinks a:hover { color: var(--ink); text-decoration: underline; }

/* The ring is the DERIVED one (`--focus-ring`), never the raw brand: a customer
   choosing a pale brand colour must not be able to make a keyboard affordance
   invisible. See packages/ui/src/theme/focus-ring.test.ts. */
.legalLinks a:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.footnote {
  margin: 0;
  font-size: var(--fs-xs);
  letter-spacing: 0.04em;
  color: var(--muted);
}

/* ── the reset affordance and the strength meter (#1032 follow-on) ─────────
   Both live in THIS file rather than a second module: they are the same screen
   arriving by another route, and #1032's finding was that a login styled from
   its own custom properties is a login no brand can reach and no dark mode
   applies to. A second stylesheet would reintroduce exactly that split. */

.forgot {
  align-self: center;
  padding: var(--sp-1) var(--sp-2);
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--fs-sm);
  color: var(--muted);
  text-decoration: underline;
  text-underline-offset: 3px;
  border-radius: var(--r-sm);
  cursor: pointer;
}

.forgot:hover { color: var(--ink); }

.forgot:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.meter {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  margin-block-start: calc(var(--sp-2) * -1);
  font-size: var(--fs-xs);
  color: var(--muted);
}

/* A real <progress>, so it is announced and it still means something with no
   CSS at all. The appearance reset is only to make it match the card. */
.meter progress {
  inline-size: 100%;
  block-size: 4px;
  appearance: none;
  border: 0;
  border-radius: var(--r-sm);
  background: var(--border);
  overflow: hidden;
}

.meter progress::-webkit-progress-bar { background: var(--border); }
.meter progress::-webkit-progress-value { background: var(--brand); }
.meter progress::-moz-progress-bar { background: var(--brand); }
